import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — the ESPR working plan 2025-2030 puts iron & steel
 * (an intermediate product) in the first delegated-act wave, expected ~Q4 2026.
 * Modeled on the ESPR Annex I information-requirement pattern, the worldsteel
 * LCI methodology, EN 10025/EN 10088 grade designation practice, and the CBAM
 * embedded-emissions reporting fields (steel is a CBAM good — the same carbon
 * data serves both the passport and the CBAM declaration, our combined value
 * proposition for Turkish exporters).
 *
 * Field ids are STABLE (persisted in passport versions); the list is trued up
 * when the delegated act lands by adding fields / flipping `required` and
 * `tier`, never by renaming ids.
 *
 * Tier logic (EN 18239 layered access): grade, recycled content and embedded
 * carbon are consumer/B2B-visible (public); mill identity, heat/cast numbers,
 * mill test certificates and CBAM CN codes are restricted to authenticated
 * roles. Reference template for the metals family (see aluminium-v1).
 */
export const steelV1: PassportSchemaDef = {
  id: "steel-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Çelik Ürün Pasaportu",
    en: "Steel Product Passport",
    de: "Stahl-Produktpass",
  },
  // Draft vocabulary: schema.org + GS1 plus an own namespace until a delegated
  // act fixes an official context.
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/steel/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.steelType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["carbon-steel", "stainless-steel", "alloy-steel", "tool-steel", "cast-iron", "other"],
          labels: { tr: "Çelik türü", en: "Steel type", de: "Stahltyp" },
        },
        {
          id: "identity.productForm",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "coil",
            "sheet",
            "plate",
            "bar",
            "rebar",
            "wire-rod",
            "tube",
            "section",
            "ingot-billet",
            "other",
          ],
          labels: { tr: "Ürün formu", en: "Product form", de: "Produktform" },
        },
        {
          id: "identity.steelGrade",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Çelik kalitesi", en: "Steel grade", de: "Stahlsorte" },
          description: "Grade designation, e.g. S235JR (EN 10025) or 304 (EN 10088).",
          highlight: true,
        },
        {
          id: "identity.brand",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
          highlight: true,
        },
        {
          id: "identity.millName",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Üretim tesisi", en: "Producing mill", de: "Herstellerwerk" },
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Döküm/parti numarası", en: "Heat/cast number", de: "Schmelzen-/Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "composition",
      labels: { tr: "Bileşim", en: "Composition", de: "Zusammensetzung" },
      fields: [
        {
          id: "composition.chemicalCompositionSummary",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Kimyasal bileşim özeti",
            en: "Chemical composition summary",
            de: "Zusammenfassung der chemischen Zusammensetzung",
          },
          description: 'Main elements by weight, e.g. "C ≤0.17, Mn ≤1.40, Si ≤0.40".',
        },
        {
          id: "composition.carbonContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Karbon içeriği", en: "Carbon content", de: "Kohlenstoffgehalt" },
        },
        {
          id: "composition.alloyingElements",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Alaşım elementleri", en: "Alloying elements", de: "Legierungselemente" },
          description: "E.g. Cr, Ni, Mo for stainless/alloy grades.",
        },
        {
          id: "composition.coatingType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "none",
            "hot-dip-galvanized",
            "electro-galvanized",
            "organic-coated",
            "tin-plated",
            "aluminized",
            "other",
          ],
          labels: { tr: "Kaplama türü", en: "Coating type", de: "Beschichtungstyp" },
        },
        {
          id: "composition.coatingThicknessMicron",
          type: "number",
          required: false,
          tier: "restricted",
          unit: "µm",
          labels: { tr: "Kaplama kalınlığı", en: "Coating thickness", de: "Beschichtungsdicke" },
        },
        {
          id: "composition.surfaceTreatment",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Yüzey işlemi", en: "Surface treatment", de: "Oberflächenbehandlung" },
        },
        {
          id: "composition.hazardousSubstancesPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Tehlikeli madde içerir",
            en: "Contains hazardous substances",
            de: "Enthält gefährliche Stoffe",
          },
        },
        {
          id: "composition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          description: "REACH candidate-list substances above 0.1% by weight (e.g. Cr(VI) in some coatings).",
        },
      ],
    },
    {
      id: "sustainability",
      labels: { tr: "Sürdürülebilirlik", en: "Sustainability", de: "Nachhaltigkeit" },
      fields: [
        {
          id: "sustainability.recycledContentPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
          highlight: true,
        },
        {
          id: "sustainability.postConsumerScrapPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tüketici sonrası hurda oranı",
            en: "Post-consumer scrap",
            de: "Post-Consumer-Schrottanteil",
          },
        },
        {
          id: "sustainability.productionRoute",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["bf-bof", "eaf", "dri-eaf", "open-hearth", "other"],
          labels: { tr: "Üretim rotası", en: "Production route", de: "Produktionsroute" },
          description:
            "BF-BOF (blast furnace), EAF (electric arc), DRI-EAF (direct-reduced) — sets the emission baseline.",
        },
        {
          id: "sustainability.embeddedCarbonKgCo2PerTonne",
          type: "number",
          required: true,
          tier: "public",
          unit: "kgCO2e/t",
          labels: {
            tr: "Gömülü emisyon (ton başına)",
            en: "Embedded emissions (per tonne)",
            de: "Eingebettete Emissionen (pro Tonne)",
          },
          description: "Cradle-to-gate embedded CO2e per tonne — shared with the CBAM declaration.",
          highlight: true,
        },
        {
          id: "sustainability.carbonFootprintMethod",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["en-15804", "iso-14067", "worldsteel-lci", "pef", "other"],
          labels: {
            tr: "Karbon ayak izi yöntemi",
            en: "Carbon footprint method",
            de: "Methode des CO2-Fußabdrucks",
          },
        },
        {
          id: "sustainability.energySource",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Enerji kaynağı", en: "Energy source", de: "Energiequelle" },
          description: "Electricity mix / fuels used in production.",
        },
        {
          id: "sustainability.cbamReported",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "CBAM raporlandı", en: "CBAM reported", de: "CBAM gemeldet" },
        },
        {
          id: "sustainability.cbamCnCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "CBAM CN kodu", en: "CBAM CN code", de: "CBAM-KN-Code" },
        },
      ],
    },
    {
      id: "performance",
      labels: { tr: "Performans", en: "Performance", de: "Leistung" },
      fields: [
        {
          id: "performance.standardConformity",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Standart uygunluğu", en: "Standard conformity", de: "Normkonformität" },
          description: "Product standard the grade conforms to, e.g. EN 10025-2.",
        },
        {
          id: "performance.yieldStrengthMpa",
          type: "number",
          required: false,
          tier: "public",
          unit: "MPa",
          labels: { tr: "Akma dayanımı", en: "Yield strength", de: "Streckgrenze" },
        },
        {
          id: "performance.tensileStrengthMpa",
          type: "number",
          required: false,
          tier: "public",
          unit: "MPa",
          labels: { tr: "Çekme dayanımı", en: "Tensile strength", de: "Zugfestigkeit" },
        },
        {
          id: "performance.elongationPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Kopma uzaması", en: "Elongation", de: "Bruchdehnung" },
        },
        {
          id: "performance.hardness",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Sertlik", en: "Hardness", de: "Härte" },
          description: "E.g. HB / HRC value.",
        },
        {
          id: "performance.heatTreatment",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Isıl işlem", en: "Heat treatment", de: "Wärmebehandlung" },
        },
        {
          id: "performance.millTestCertificateUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Muayene sertifikası (EN 10204)",
            en: "Mill test certificate (EN 10204)",
            de: "Abnahmeprüfzeugnis (EN 10204)",
          },
          description: "EN 10204 3.1/3.2 inspection certificate.",
        },
        {
          id: "performance.intendedUse",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Kullanım amacı", en: "Intended use", de: "Verwendungszweck" },
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
        {
          id: "circularity.reusePotential",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yeniden kullanım potansiyeli",
            en: "Reuse potential",
            de: "Wiederverwendungspotenzial",
          },
        },
        {
          id: "circularity.endOfLifeGuidance",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu yönlendirmesi",
            en: "End-of-life guidance",
            de: "Hinweise zum Lebensende",
          },
          description: "How to return the product to the scrap/recycling stream.",
        },
        {
          id: "circularity.scrapSeparationGuidance",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Hurda ayrıştırma rehberi",
            en: "Scrap separation guidance",
            de: "Anleitung zur Schrotttrennung",
          },
          description: "For recyclers: coating removal, alloy sorting.",
        },
        {
          id: "circularity.hazardousCoatingRemoval",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tehlikeli kaplama çıkarma",
            en: "Hazardous coating removal",
            de: "Entfernung gefährlicher Beschichtungen",
          },
        },
      ],
    },
  ],
};
